import { captureWriteIdentity, type WriteIdentity } from '@/core/api/http'

import {
  getParticipant,
  getCoachingVertragRef,
  getTeilnahmeDates,
  setAufgabe,
} from '@/core/data/coachings'
import { getDocData, getDocBucketAddress, getDocSignedAt, patchDocData, upsertDoc } from '@/core/data/dokumente'
import { formatDatum } from '@/core/lib/dates'
import { realUe } from '../lib/teilnahmeEnde'
import { isPendingKundennummer } from '@/core/lib/kundennummer'
import { renderSignedDocument } from '@/core/api/documentsRender'
import { createParticipantLink, type ParticipantLinkMinted } from '@/core/api/portal'

const VERTRAG = 'vertrag'
const pendingVertrag = new Map<string, Promise<void>>()

function inVertragOrder<T>(coachingId: string, operation: () => Promise<T>): Promise<T> {
  const result = (pendingVertrag.get(coachingId) ?? Promise.resolve()).then(operation)
  const settled = result.then(() => {}, () => {})
  pendingVertrag.set(coachingId, settled)
  void settled.then(() => {
    if (pendingVertrag.get(coachingId) === settled) pendingVertrag.delete(coachingId)
  })
  return result
}

const s = (v: unknown) => (v == null ? '' : String(v))

function formatAnschrift(p: {
  strasse: string | null
  hausnummer: string | null
  plz: string | null
  ort: string | null
}): string {
  const street = [s(p.strasse), s(p.hausnummer)].filter(Boolean).join(' ')
  const city = [s(p.plz), s(p.ort)].filter(Boolean).join(' ')
  return [street, city].filter(Boolean).join(', ')
}

function formatEuro(value: unknown): string {
  const n = Number(value)
  if (!Number.isFinite(n)) return ''
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(n)
}

export async function buildVertragData(coachingId: string): Promise<Record<string, unknown>> {
  const [participant, ref, dates] = await Promise.all([
    getParticipant(coachingId),
    getCoachingVertragRef(coachingId),
    getTeilnahmeDates(coachingId),
  ])

  const massnahme = ref?.massnahme ?? null

  const nachname = s(participant?.nachname).trim()
  const vorname = s(participant?.vorname).trim()
  const kostentraeger = [s(ref?.agentur?.behoerde), s(ref?.agentur?.standort)]
    .filter(Boolean)
    .join(' ')
  const rawKn = s(participant?.kundennummer)
  const kundennummer = isPendingKundennummer(rawKn) ? '' : rawKn
  const lfdNr = ref?.avgs_nummer ?? 0
  const avgsNummer = kundennummer && lfdNr > 0 ? `${kundennummer} - ${lfdNr}` : ''

  return {
    teilnehmer: {
      nameVorname: nachname && vorname ? `${nachname}, ${vorname}` : nachname || vorname,
      anschrift: participant ? formatAnschrift(participant) : '',
      geburtsdatum: formatDatum(participant?.geburtsdatum),
    },
    kostentraeger,
    kundennummer,
    avgsNummer,
    massnahme: {
      titel: s(massnahme?.bezeichnung),
      nummer: s(massnahme?.nummer),
      abschluss: s(massnahme?.abschluss),
      einheiten:
        massnahme != null
          ? String(
              ref && ref.zeitl_umf_max_ue > 0
                ? realUe(massnahme.ue_anzahl, ref.zeitl_umf_max_ue)
                : massnahme.ue_anzahl,
            )
          : '',
      kosten: formatEuro(massnahme?.kosten),
      beginn: formatDatum(dates.beginn),
      ende: formatDatum(dates.ende),
    },
  }
}

function deepMerge(
  base: Record<string, unknown>,
  over: Record<string, unknown>,
): Record<string, unknown> {
  const out: Record<string, unknown> = { ...base }
  for (const [k, v] of Object.entries(over)) {
    const b = out[k]
    if (v && typeof v === 'object' && !Array.isArray(v) && b && typeof b === 'object' && !Array.isArray(b)) {
      out[k] = deepMerge(b as Record<string, unknown>, v as Record<string, unknown>)
    } else {
      out[k] = v
    }
  }
  return out
}

export async function loadVertragCorrections(
  coachingId: string,
): Promise<Record<string, unknown> | null> {
  return inVertragOrder(coachingId, async () => {
    const d = await getDocData(coachingId, VERTRAG)
    return (d?.corrections as Record<string, unknown> | undefined) ?? null
  })
}

export async function saveVertragCorrections(
  coachingId: string,
  corrections: Record<string, unknown>,
  identity: WriteIdentity = captureWriteIdentity(),
): Promise<void> {
  await inVertragOrder(coachingId, () => patchDocData(coachingId, VERTRAG, { corrections }, undefined, identity))
}

export async function buildVertragRenderData(coachingId: string): Promise<Record<string, unknown>> {
  const [base, corrections] = await Promise.all([
    buildVertragData(coachingId),
    loadVertragCorrections(coachingId),
  ])
  return corrections ? deepMerge(base, corrections) : base
}

export type VertragGenerateResult =
  | { status: 'signed'; path: string; signedAt: string }
  | { status: 'generated'; path: string; traegerSigned: boolean }

export function vertragSignedResult(
  signedAt: string | null,
  bucketAddress: string | null,
): VertragGenerateResult | null {
  return signedAt ? { status: 'signed', path: bucketAddress ?? '', signedAt } : null
}

export class VertragBereitsSigniertError extends Error {
  constructor() {
    super("Der Vertrag ist bereits signiert.")
    this.name = 'VertragBereitsSigniertError'
  }
}

export async function getVertragSignedAt(coachingId: string): Promise<string | null> {
  return inVertragOrder(coachingId, () => getDocSignedAt(coachingId, VERTRAG))
}

export async function generateAndStoreVertrag(coachingId: string): Promise<VertragGenerateResult> {
  return inVertragOrder<VertragGenerateResult>(coachingId, async () => {
    const signedAt = await getDocSignedAt(coachingId, VERTRAG)
    const signed = vertragSignedResult(signedAt, signedAt ? await getDocBucketAddress(coachingId, VERTRAG) : null)
    if (signed) return signed

    const { dokument, traeger_signature_embedded } = await renderSignedDocument('vertrag', coachingId)
    return {
      status: 'generated',
      path: dokument.bucket_address,
      traegerSigned: traeger_signature_embedded,
    }
  })
}

export async function confirmVertragAndPublish(coachingId: string): Promise<ParticipantLinkMinted> {
  const signedAt = await getVertragSignedAt(coachingId)
  if (signedAt) {
    throw new VertragBereitsSigniertError()
  }
  const link = await createParticipantLink(coachingId, 'vertrag')
  await upsertDoc(coachingId, VERTRAG, { teilnehmerSichtbar: true })
  await setAufgabe(coachingId, 'vertragUnterschreiben')
  return link
}
